import { AttachmentInput } from "@/components/AttachmentInput"
import { LinkButtonWithIcon } from "@/components/Buttons/LinkButtonWithIcon"
import { QuestionCircleIcon, TelegramAltIcon } from "@/components/Icons"
import { RequiredErrorMessage } from "@/components/RequiredErrorMessage"
import SingleField from "@/components/Sections/SingleField"
import { SelectOption } from "@/components/Select/SelectOptions/SelectOption"
import { H3, P3 } from "@/components/Typographies"
import { ROOT_PATH } from "@/domain/resources/constants"
import { useAccessToken } from "@/hooks/useAccessToken"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import {
  type HelpdeskPostHelpdeskInputHelpdeskCreate,
  useListFile,
  usePostHelpdesk,
} from "@/networking/api"
import { openFileLink } from "@/utils/download"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import { navigateToBlank } from "@/utils/urlUtils"
import { Box, Button, Flex, FormControl, Input, Select, Textarea, chakra } from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"

const reasonLabels = [
  "mail",
  "apimo",
  "hubspot",
  "mybarnes",
  "realestateplatform",
  "website",
  "other",
]

export const Help: React.FC = () => {
  const intl = useIntl()
  const errorToast = useGenericErrorToast()
  const [messageSent, setMessageSent] = useState<boolean>(false)

  // Form

  const modalForm = useForm<HelpdeskPostHelpdeskInputHelpdeskCreate>()
  const {
    handleSubmit,
    formState: { errors },
    register,
    reset,
  } = modalForm

  const { mutateAsync, isPending, reset: resetPost } = usePostHelpdesk()
  const onSubmit = (formData: HelpdeskPostHelpdeskInputHelpdeskCreate) => {
    // console.log("submit:formData", formData)
    // console.log("submit:getValues", getValues())

    mutateAsync(
      { data: formData },
      {
        onSuccess: () => setMessageSent(true),
        onError: errorToast,
      }
    )
  }

  // FAQ files

  const { accessToken } = useAccessToken()
  const { data: userInfos } = useGetUserInfos()
  const lang = (userInfos?.locale.split("_").shift() ?? "en").toLocaleUpperCase()

  const { data: dataFiles, isFetching: isFAQLoading } = useListFile(ROOT_PATH)
  const listFiles = useMemo(
    () =>
      (dataFiles?.["hydra:member"] ?? []).filter(f =>
        f.path?.match(new RegExp(`FAQ\-MBV4\-${lang}`))
      ),
    [dataFiles, lang]
  )
  const fileFAQ = listFiles[0]

  return (
    <>
      <Flex flexDir="row" gap="6" px="8" py="12" justifyContent="space-between">
        <H3>
          <FormattedMessage id="resources.help" />
        </H3>
        <Flex flexDir="row" gap="6" mr="0" ml="auto">
          {isNotNullNorUndefined(fileFAQ) && (
            <Button
              onClick={() => openFileLink(fileFAQ, accessToken)}
              variant="outline"
              isLoading={isFAQLoading}
            >
              <FormattedMessage id="resources.help.download_faq" />
            </Button>
          )}
          <LinkButtonWithIcon
            title="User Guide"
            variant="outline"
            iconSize={5}
            fontWeight="medium"
            color="text"
            icon={QuestionCircleIcon}
            onClick={() =>
              navigateToBlank(
                "https://barnes-international.notion.site/BARNES-SUPPORT-ECOSYSTEM-358ed5a44e49808bada8f2b8bd2e19e1"
              )
            }
          />
        </Flex>
      </Flex>

      {messageSent ? (
        <Flex flexDir="column" gap="6" px="8" py="12">
          <P3>
            <FormattedMessage id="resources.help.success.message" />
          </P3>
          <Button
            display="flex"
            gap="2"
            onClick={() => {
              reset()
              resetPost()
              setMessageSent(false)
            }}
            mx="auto"
          >
            <FormattedMessage id="resources.help.resend" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </Flex>
      ) : (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <Flex flexDir="column" gap="6" px="8" py="12">
            <SingleField label={intl.formatMessage({ id: "resources.help.reason" })}>
              <FormControl as="div" isInvalid={!!errors.reason}>
                <Select
                  {...register("reason", { required: true })}
                  defaultValue={intl.formatMessage({ id: "resources.help.reason.other" })}
                >
                  {reasonLabels.map(option => (
                    <SelectOption
                      key={option}
                      value={intl.formatMessage({ id: `resources.help.reason.${option}` })}
                    />
                  ))}
                </Select>
                <RequiredErrorMessage />
              </FormControl>
            </SingleField>
            <SingleField label={intl.formatMessage({ id: "resources.help.subject" })}>
              <FormControl as="div" isInvalid={!!errors.subject}>
                <Input {...register("subject", { required: true })} />
                <RequiredErrorMessage />
              </FormControl>
            </SingleField>
            <SingleField label={intl.formatMessage({ id: "resources.help.message" })}>
              <FormControl as="div" isInvalid={!!errors.description}>
                <Textarea
                  {...register("description", { required: false })}
                  rows={6}
                  resize="none"
                />
              </FormControl>
            </SingleField>
            <SingleField label={intl.formatMessage({ id: "resources.help.attachment" })}>
              <FormControl as="div" isInvalid={!!errors.file}>
                <AttachmentInput {...register("file")} modalForm={modalForm} />
              </FormControl>
            </SingleField>

            <Flex flexDir="column" gap="6" px="8" pt="12" alignItems="center">
              <Button display="flex" gap="2" type="submit" isLoading={isPending} width="auto">
                <FormattedMessage id="resources.help.send" />
                <TelegramAltIcon boxSize={4} />
              </Button>
            </Flex>

            <Box px="8" py="12" textAlign="center" color="textMuted" fontStyle="italic">
              <FormattedMessage id="resources.help.text_other_comm" />
              <chakra.a href="mailto:support@barnes-international.com" textDecoration="underline">
                support@barnes-international.com
              </chakra.a>
            </Box>
          </Flex>
        </Box>
      )}
    </>
  )
}
